import { useEffect, useState } from "react";
import { Button, ButtonVariant, Divider } from "@merge-api/merge-javascript-shared";
import { ChevronLeft, RefreshCw } from "lucide-react";
import useBlueprintContext from "../context/useBlueprintContext";
import CodeBlockPicker from "./CodeBlockPicker";
import CodeBlockVersionSwapConfirmDialog from "./CodeBlockVersionSwapConfirmDialog";
import {
  getBlueprintStepForStepID,
  getCodeBlockParameterBindingChanges,
  CodeBlockParameterBindingChange,
} from "../utils/BlueprintEditorUtils";
import { fetchCodeBlockGitHubUrl, fetchCodeBlockTemplate } from "../utils/BlueprintEditorAPIClient";
import { showErrorToast } from "../../shared/Toasts";
import BlueprintEditorRightPanelGenericStepForm from "./BlueprintEditorRightPanelGenericStepForm";

interface Props {
  stepID: string;
}

interface PendingSwap {
  codeBlockName: string;
  version: string;
  template: any;
  githubUrl: string | null;
  bindingChanges: CodeBlockParameterBindingChange[];
}

const BlueprintEditorRightPanelCodeBlockForm = ({ stepID }: Props) => {
  const { blueprint, replaceStepTemplate, codeBlockBranch } = useBlueprintContext();
  const step = getBlueprintStepForStepID(blueprint, stepID);

  // state
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [currentGithubUrl, setCurrentGithubUrl] = useState<string | null>(null);
  const [isLoadingSwapPreview, setIsLoadingSwapPreview] = useState(false);
  const [pendingSwap, setPendingSwap] = useState<PendingSwap | null>(null);

  // derived state
  const currentCodeBlockName = step?.template?.metadata?.code_block;
  const currentCodeBlockVersion = step?.template?.metadata?.version;

  const hasCodeBlockSelected = !!currentCodeBlockName && !!currentCodeBlockVersion;

  // effects
  // Fetch the current version's GitHub source link for display in the header.
  useEffect(() => {
    if (!currentCodeBlockName || !currentCodeBlockVersion) {
      setCurrentGithubUrl(null);
      return;
    }
    fetchCodeBlockGitHubUrl({
      blockName: currentCodeBlockName,
      versionName: currentCodeBlockVersion,
      branch: codeBlockBranch,
      onSuccess: (data) => setCurrentGithubUrl(data.github_url),
      onError: () => setCurrentGithubUrl(null),
    });
  }, [currentCodeBlockName, currentCodeBlockVersion, codeBlockBranch]);

  if (!step) {
    return <div>Step not found</div>;
  }

  // event handlers
  const applySwap = (swap: PendingSwap) => {
    const updatedTemplate = {
      ...swap.template,
      id: step.template.id,
    };
    replaceStepTemplate(step, updatedTemplate);
    setPendingSwap(null);
    setIsChangingVersion(false);
  };

  const onPickVersion = (codeBlockName: string, version: string) => {
    if (codeBlockName === currentCodeBlockName && version === currentCodeBlockVersion) {
      setIsChangingVersion(false);
      return;
    }

    setIsLoadingSwapPreview(true);

    const templatePromise = new Promise<any>((resolve) => {
      fetchCodeBlockTemplate({
        blockName: codeBlockName,
        versionName: version,
        branch: codeBlockBranch,
        onSuccess: (data) => resolve(data),
        onError: () => {
          showErrorToast(`Failed to fetch template for ${codeBlockName} version ${version}`);
          resolve(null);
        },
      });
    });

    const githubUrlPromise = new Promise<string | null>((resolve) => {
      fetchCodeBlockGitHubUrl({
        blockName: codeBlockName,
        versionName: version,
        branch: codeBlockBranch,
        onSuccess: (data) => resolve(data.github_url),
        onError: () => {
          showErrorToast(`Failed to fetch GitHub URL for ${codeBlockName} version ${version}`);
          resolve(null);
        },
      });
    });

    Promise.all([templatePromise, githubUrlPromise]).then(([template, githubUrl]) => {
      setIsLoadingSwapPreview(false);
      if (!template) {
        return;
      }
      const bindingChanges = getCodeBlockParameterBindingChanges(step.template, template, step);
      setPendingSwap({ codeBlockName, version, template, githubUrl, bindingChanges });
    });
  };

  // render
  return (
    <div className="flex flex-col gap-y-3">
      {hasCodeBlockSelected && !isChangingVersion && (
        <>
          <Button
            variant={ButtonVariant.SecondaryGray}
            fullWidth
            leftIcon={<RefreshCw size={16} />}
            onClick={() => setIsChangingVersion(true)}
          >
            Change code block/version
          </Button>
          <Divider />
        </>
      )}

      {(!hasCodeBlockSelected || isChangingVersion) && (
        <>
          {hasCodeBlockSelected && (
            <>
              <Button
                variant={ButtonVariant.TextBlack}
                leftIcon={<ChevronLeft size={16} />}
                onClick={() => setIsChangingVersion(false)}
              >
                Back
              </Button>
              <Divider />
            </>
          )}
          <CodeBlockPicker
            initialCodeBlockName={currentCodeBlockName}
            initialVersion={currentCodeBlockVersion}
            submitButtonText={hasCodeBlockSelected ? "Preview change" : "Add code block step"}
            onSelect={onPickVersion}
          />
        </>
      )}

      <CodeBlockVersionSwapConfirmDialog
        open={isLoadingSwapPreview || !!pendingSwap}
        isLoading={isLoadingSwapPreview}
        onClose={() => {
          setPendingSwap(null);
          setIsLoadingSwapPreview(false);
        }}
        onConfirm={() => pendingSwap && applySwap(pendingSwap)}
        currentCodeBlockName={currentCodeBlockName ?? ""}
        currentVersion={currentCodeBlockVersion ?? ""}
        newCodeBlockName={pendingSwap?.codeBlockName ?? ""}
        newVersion={pendingSwap?.version ?? ""}
        currentGithubUrl={currentGithubUrl}
        newGithubUrl={pendingSwap?.githubUrl ?? null}
        bindingChanges={pendingSwap?.bindingChanges ?? []}
      />

      {hasCodeBlockSelected && !isChangingVersion && step.template && (
        <BlueprintEditorRightPanelGenericStepForm stepID={stepID} stepTemplate={step.template} />
      )}
    </div>
  );
};

export default BlueprintEditorRightPanelCodeBlockForm;
